import type { CSSProperties } from 'react';
import type { GuestTag } from '../types';

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const SHORTCODE_RE = /^[a-z0-9]+(?:[_-][a-z0-9]+)+$/;
const LEADING_SHORTCODE_RE = /^([a-z0-9]+(?:[_-][a-z0-9]+)+)(?:\s+|$)(.*)$/s;
const COLON_SHORTCODE_RE = /^:([a-z0-9]+(?:[_-][a-z0-9]+)+):$/;

/**
 * gemoji / emoji-mart ids Aleno prefixes onto tag names
 * (e.g. ``smiling_face_with_3_hearts Stammgäste``).
 */
const SHORTCODE_ALIASES: Record<string, string> = {
  smiling_face_with_3_hearts: '🥰',
  star_struck: '🤩',
  heart_eyes: '😍',
  heart_eyes_cat: '😻',
  kissing_heart: '😘',
  two_hearts: '💕',
  sparkling_heart: '💖',
  revolving_hearts: '💞',
  heartbeat: '💓',
  heartpulse: '💗',
  cupid: '💘',
  gift_heart: '💝',
  heart: '❤️',
  orange_heart: '🧡',
  yellow_heart: '💛',
  green_heart: '💚',
  blue_heart: '💙',
  purple_heart: '💜',
  black_heart: '🖤',
  white_heart: '🤍',
  brown_heart: '🤎',
  broken_heart: '💔',
  star: '⭐',
  glowing_star: '🌟',
  fire: '🔥',
  warning: '⚠️',
  exclamation: '❗',
  crown: '👑',
  trophy: '🏆',
  medal: '🏅',
  wine_glass: '🍷',
  wine: '🍷',
  champagne: '🍾',
  clinking_glasses: '🥂',
  beer: '🍺',
  beers: '🍻',
  cocktail: '🍸',
  tropical_drink: '🍹',
  coffee: '☕',
  tea: '🍵',
  fork_and_knife: '🍴',
  fork_knife_plate: '🍽️',
  plate_with_cutlery: '🍽️',
  pizza: '🍕',
  hamburger: '🍔',
  sushi: '🍣',
  cake: '🍰',
  birthday: '🎂',
  gift: '🎁',
  balloon: '🎈',
  tada: '🎉',
  confetti_ball: '🎊',
  sparkles: '✨',
  thumbsup: '👍',
  thumbs_up: '👍',
  thumbsdown: '👎',
  thumbs_down: '👎',
  ok_hand: '👌',
  clap: '👏',
  wave: '👋',
  pray: '🙏',
  handshake: '🤝',
  muscle: '💪',
  white_check_mark: '✅',
  heavy_check_mark: '✔️',
  no_entry: '⛔',
  no_entry_sign: '🚫',
  sos: '🆘',
  rotating_light: '🚨',
  bell: '🔔',
  memo: '📝',
  speech_balloon: '💬',
  busts_in_silhouette: '👥',
  family: '👪',
  baby: '👶',
  dog: '🐶',
  cat: '🐱',
  airplane: '✈️',
  hotel: '🏨',
  moneybag: '💰',
  credit_card: '💳',
  briefcase: '💼',
  necktie: '👔',
  gem: '💎',
};

/** Accept only validated CSS hex colors from the Live API. */
export function sanitizeTagColor(color: string | null | undefined): string | null {
  if (!color || typeof color !== 'string') return null;
  const text = color.trim();
  return HEX_RE.test(text) ? text.toLowerCase() : null;
}

function looksLikeShortcode(token: string): boolean {
  return SHORTCODE_RE.test(token);
}

export function resolveEmojiShortcode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const colon = COLON_SHORTCODE_RE.exec(trimmed);
  const token = (colon?.[1] ?? trimmed).toLowerCase().replace(/-/g, '_');
  const glyph = SHORTCODE_ALIASES[token];
  if (glyph) return glyph;
  if (!looksLikeShortcode(token) && /[^\u0000-\u007f]/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

function splitLeadingEmoji(name: string): { emoji: string | null; rest: string } {
  const first = name[0];
  if (!first || (first >= 'A' && first <= 'z' && first !== '_')) {
    return { emoji: null, rest: name };
  }
  // Peel one extended pictographic + optional VS16 / ZWJ / skin tone.
  const match = name.match(
    /^(?:\p{Extended_Pictographic}|\p{Symbol})(?:\uFE0F|\u20E3|\u200D|[\u{1F3FB}-\u{1F3FF}])*/u,
  );
  if (!match?.[0] || /^[\u0000-\u007f]+$/.test(match[0])) {
    return { emoji: null, rest: name };
  }
  return { emoji: match[0], rest: name.slice(match[0].length).trim() };
}

/** Peel Aleno CLDR prefixes so chips show ``🥰 Stammgäste`` not the shortcode. */
export function humanizeGuestTag(
  tag: Pick<GuestTag, 'name' | 'emoji'>,
): { name: string; emoji?: string | null } {
  const label = tag.name.replace(/\s+/g, ' ').trim();
  let emoji = resolveEmojiShortcode(tag.emoji);

  if (!label) return { name: '', emoji };

  const leading = splitLeadingEmoji(label);
  if (leading.emoji) {
    return { name: leading.rest || label, emoji: emoji ?? leading.emoji };
  }

  const prefixed = LEADING_SHORTCODE_RE.exec(label);
  if (prefixed && looksLikeShortcode(prefixed[1]!)) {
    const rest = (prefixed[2] ?? '').trim();
    const glyph = resolveEmojiShortcode(prefixed[1]);
    emoji = emoji ?? glyph;
    if (rest) return { name: rest, emoji };
    return { name: emoji || label, emoji };
  }

  return { name: label, emoji };
}

export function tagLabel(tag: Pick<GuestTag, 'name' | 'emoji'>): string {
  const { name, emoji } = humanizeGuestTag(tag);
  const mark = emoji?.trim();
  if (!mark) return name;
  if (!name || name === mark || name.startsWith(`${mark} `)) return name || mark;
  return `${mark} ${name}`;
}

/** Soft chip accent from Aleno tag color (border + wash; readable fallback). */
export function guestTagChipStyle(
  color: string | null | undefined,
): CSSProperties | undefined {
  const hex = sanitizeTagColor(color);
  if (!hex) return undefined;
  return {
    borderColor: `color-mix(in srgb, ${hex} 45%, var(--line))`,
    background: `color-mix(in srgb, ${hex} 14%, var(--surface))`,
  };
}

export function formatGuestTagsTip(tags: GuestTag[] | null | undefined): string | null {
  if (!tags?.length) return null;
  const labels = tags
    .map(t => tagLabel(t).trim())
    .filter(Boolean)
    .slice(0, 6);
  if (!labels.length) return null;
  const extra = tags.length > labels.length ? ` +${tags.length - labels.length}` : '';
  return `Tags: ${labels.join(', ')}${extra}`;
}

/** First resolved tag emoji for a Gantt bar (one glyph so the lane stays scannable). */
export function barTagEmoji(tags: GuestTag[] | null | undefined): string | null {
  if (!tags?.length) return null;
  for (const tag of tags) {
    const mark = humanizeGuestTag(tag).emoji?.trim();
    if (mark) return mark;
  }
  return null;
}
